/* Email pop-up (sections/email-toast.liquid).

   Opens once the shopper scrolls part way down (or after a delay). Closing it
   leaves a small reopen tab in the corner; both stay away for the section's
   "hide for" days once dismissed. After signing up, closing it leaves a "your
   code" tab instead, which reopens on the code - on every page, until it is
   dismissed or CODE_TAB_DAYS pass.

   Sign-up happens in the background, without leaving the page. Shopify guards
   customer forms with an invisible hCaptcha: its script intercepts the submit,
   fetches a token into the form, then calls the form's own submit() - which it
   captures when it first binds to the form (on focus). We replace submit() on
   this form before that, so Shopify ends up calling sendInBackground() with
   the token already in the form, and we post it with fetch.

   If Shopify's captcha is not on the page at all, it falls back to a normal
   submit. A session flag (holding the scroll position) marks that,
   and on the page Shopify redirects back to, the toast reopens on the code (or
   on Shopify's error) with the page where the shopper left it. */

if (!customElements.get('email-toast')) {
  const STORAGE_KEY = 'eb-email-toast';
  const PENDING_KEY = 'eb-email-toast-pending';
  const RETURN_HASH = '#email-signup';
  const DAY = 24 * 60 * 60 * 1000;
  const GENERIC_ERROR = 'Something went wrong. Please try again.';
  const CODE_TAB_DAYS = 30;

  const store = {
    read() {
      try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
      } catch (e) {
        return {};
      }
    },
    write(value) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
      } catch (e) {}
    },
  };

  // Scroll position at a normal submit, or null when this load is not a return.
  const pending = {
    get() {
      try {
        const value = sessionStorage.getItem(PENDING_KEY);
        return value === null ? null : Number(value) || 0;
      } catch (e) {
        return null;
      }
    },
    set(value) {
      try {
        value === null ? sessionStorage.removeItem(PENDING_KEY) : sessionStorage.setItem(PENDING_KEY, String(value));
      } catch (e) {}
    },
  };

  class EmailToast extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.signup = this.querySelector('[data-email-toast-step="signup"]');
      this.success = this.querySelector('[data-email-toast-step="success"]');
      this.error = this.querySelector('[data-email-toast-error]');
      this.input = this.form.querySelector('input[type="email"]');
      this.submitButton = this.form.querySelector('[data-email-toast-submit]');
      this.dialog = this.querySelector('[data-email-toast-dialog]');
      this.tab = this.querySelector('[data-email-toast-tab]');
      const posted = this.signup.dataset.posted === 'true';

      // Must happen before Shopify's captcha binds to the form (see top).
      this.form.submit = () => this.sendInBackground();

      this.querySelector('[data-email-toast-close]').addEventListener('click', () => this.close());
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
      this.querySelector('[data-email-toast-copy]')?.addEventListener('click', () => this.copyCode());
      this.dialog.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
      });
      this.querySelector('[data-email-toast-reopen]')?.addEventListener('click', () => this.reopen());
      this.querySelector('[data-email-toast-tab-close]')?.addEventListener('click', () => this.dismissTab());

      // Shopify's challenge page (visible captcha) renders the footer too. Stay
      // out of the way, and leave the pending flag for the page it returns to.
      if (location.pathname === '/challenge') return;

      if (this.hasAttribute('data-design-mode')) {
        const preview = this.dataset.designMode;
        if (preview === 'tab' && this.tab) return this.showTab(false, 'offer');
        this.open();
        if (preview === 'success') this.showSuccess(false);
        return;
      }

      const returnScroll = pending.get();
      if (returnScroll !== null) {
        pending.set(null);
        this.restorePage(returnScroll);
        if (posted) {
          this.markSubscribed(true);
          this.open();
          this.showSuccess(true);
          return;
        }
        if (this.error.textContent.trim()) {
          this.open();
          setTimeout(() => this.input.focus({ preventScroll: true }), 50);
          return;
        }
      }

      // Signed up through another email form (e.g. the footer): already subscribed.
      if (posted) {
        this.markSubscribed();
        return;
      }

      const state = store.read();
      if (state.subscribed) {
        const fresh = Date.now() - (state.subscribedAt || 0) < CODE_TAB_DAYS * DAY;
        if (state.codeTab && fresh) this.showTab(false, 'code');
        return;
      }
      if (this.isRecent(state.dismissedAt)) {
        if (!this.isRecent(state.tabDismissedAt)) this.showTab();
        return;
      }

      this.arm();
    }

    disconnectedCallback() {
      this.disarm();
    }

    isRecent(time) {
      return Boolean(time) && Date.now() - time < Number(this.dataset.dismissDays) * DAY;
    }

    // Open once the shopper scrolls far enough down, or after a delay.
    arm() {
      if (this.dataset.trigger !== 'scroll') {
        this.timer = setTimeout(() => this.trigger(), Number(this.dataset.delay) * 1000);
        return;
      }

      const depth = Number(this.dataset.scrollDepth) / 100;
      const root = document.documentElement;
      const scrollable = () => root.scrollHeight - window.innerHeight;

      // A page too short to scroll that far would never trigger it.
      this.timer = setTimeout(() => {
        if (scrollable() * depth < 200) {
          this.disarm();
          this.trigger();
        }
      }, 10000);

      this.onScroll = () => {
        if (this.scrollQueued) return;
        this.scrollQueued = true;
        requestAnimationFrame(() => {
          this.scrollQueued = false;
          if (window.scrollY >= scrollable() * depth) {
            this.disarm();
            this.trigger();
          }
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }

    // On phones (when the section asks for it) the trigger brings in the small
    // offer tab rather than the full pop-up; tapping the tab opens it.
    trigger() {
      const phone = window.matchMedia('(max-width: 749px)').matches;
      if (phone && this.hasAttribute('data-mobile-tab') && this.tab) return this.showTab(false, 'offer');
      this.open();
    }

    disarm() {
      clearTimeout(this.timer);
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      this.onScroll = null;
    }

    // Shopify marks every customer form on the page as posted, so the footer
    // newsletter also renders its success or error message. The sign-up was
    // ours: hide those and put the page back where the shopper was.
    restorePage(scrollY) {
      if (location.hash === RETURN_HASH) history.replaceState(null, '', location.pathname + location.search);
      document.querySelectorAll('.newsletter-form__message').forEach((message) => {
        if (!this.contains(message)) message.hidden = true;
      });
      const settle = () => window.scrollTo(0, scrollY);
      settle();
      setTimeout(settle, 50);
    }

    // Shared show/hide for the dialog and the tab, with the slide transition.
    reveal(element) {
      element.hidden = false;
      requestAnimationFrame(() => requestAnimationFrame(() => element.classList.add('is-open')));
    }

    conceal(element) {
      element.classList.remove('is-open');
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      return new Promise((resolve) =>
        setTimeout(() => {
          element.hidden = true;
          resolve();
        }, reduced ? 0 : 300)
      );
    }

    open() {
      this.reveal(this.dialog);
    }

    async close() {
      const subscribed = store.read().subscribed;
      if (!this.hasAttribute('data-design-mode') && !subscribed) {
        store.write({ ...store.read(), dismissedAt: Date.now() });
      }
      const hadFocus = this.dialog.contains(document.activeElement);
      await this.conceal(this.dialog);
      if (!subscribed) this.showTab(hadFocus, 'offer');
      else if (store.read().codeTab) this.showTab(hadFocus, 'code');
    }

    // mode 'offer' reopens the sign-up; 'code' reopens on the discount code.
    showTab(moveFocus, mode) {
      if (!this.tab || !this.hasAttribute('data-show-tab')) return;
      const button = this.querySelector('[data-email-toast-reopen]');
      this.tab.dataset.mode = mode;
      button.textContent = mode === 'code' ? button.dataset.codeLabel : button.dataset.offerLabel;
      this.reveal(this.tab);
      if (moveFocus) button.focus({ preventScroll: true });
    }

    async reopen() {
      const code = this.tab.dataset.mode === 'code';
      await this.conceal(this.tab);
      if (code) this.showSuccess(false);
      this.open();
      const target = code ? this.querySelector('[data-email-toast-success-heading]') : this.input;
      setTimeout(() => target.focus({ preventScroll: true }), 60);
    }

    dismissTab() {
      if (!this.hasAttribute('data-design-mode')) {
        const state = store.read();
        if (this.tab.dataset.mode === 'code') store.write({ ...state, codeTab: false });
        else store.write({ ...state, tabDismissedAt: Date.now() });
      }
      this.conceal(this.tab);
    }

    // withCode: signed up through this toast, so keep the code within reach.
    markSubscribed(withCode = false) {
      const state = store.read();
      store.write({ ...state, subscribed: true, subscribedAt: Date.now(), codeTab: withCode || Boolean(state.codeTab) });
    }

    showSuccess(moveFocus) {
      this.form.hidden = true;
      this.success.hidden = false;
      if (!moveFocus) return;
      const heading = this.querySelector('[data-email-toast-success-heading]');
      setTimeout(() => heading.focus({ preventScroll: true }), 60);
    }

    setBusy(busy) {
      this.submitButton.disabled = busy;
      busy ? this.submitButton.setAttribute('aria-busy', 'true') : this.submitButton.removeAttribute('aria-busy');
    }

    onSubmit(event) {
      if (!this.input.checkValidity()) {
        // Ours runs first, so this also keeps Shopify from starting the captcha.
        event.preventDefault();
        event.stopImmediatePropagation();
        this.error.textContent = this.input.validationMessage;
        this.input.setAttribute('aria-invalid', 'true');
        this.input.focus();
        return;
      }

      this.error.textContent = '';
      this.input.removeAttribute('aria-invalid');
      this.setBusy(true);

      // Normally Shopify's captcha cancels this submit and later calls
      // sendInBackground(). If it is not on the page, this becomes a normal
      // submit; prepare for the return trip.
      this.prepareNativeSubmit();

      // Safety net: if the submit was cancelled and nothing followed, re-enable.
      clearTimeout(this.busyTimer);
      this.busyTimer = setTimeout(() => {
        if (!this.sending) this.setBusy(false);
      }, 15000);
    }

    prepareNativeSubmit() {
      // The browser carries the action's #fragment through Shopify's redirect and
      // would scroll to the toast's spot at the foot of the page.
      this.form.action = this.form.action.split('#')[0] + RETURN_HASH;
      pending.set(window.scrollY);
    }

    showError(message) {
      this.error.textContent = message;
      this.input.setAttribute('aria-invalid', 'true');
      this.input.focus();
    }

    // Called by Shopify's captcha once it has put a token in the form. A token
    // is single-use, so this never falls back to a normal submit - that would
    // resend the spent token and land on Shopify's "Invalid CAPTCHA token" page.
    async sendInBackground() {
      if (this.sending) return;
      this.sending = true;
      pending.set(null);
      this.setBusy(true);

      try {
        const response = await fetch(this.form.action, {
          method: 'POST',
          body: new FormData(this.form),
          headers: { Accept: 'text/html' },
        });

        if (response.url.includes('customer_posted=true')) {
          this.markSubscribed(true);
          this.showSuccess(true);
          return;
        }

        // Shopify wants a visible captcha. Its challenge page picks the form up
        // from session storage (the form_key in the URL), and after it is solved
        // Shopify returns here with customer_posted=true; the pending flag makes
        // the toast reopen on the code then.
        if (new URL(response.url).pathname === '/challenge') {
          this.leaving = true;
          pending.set(window.scrollY);
          window.location.href = response.url;
          return;
        }

        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const message = doc
          .querySelector(`email-toast[data-section-id="${this.dataset.sectionId}"] [data-email-toast-error]`)
          ?.textContent.trim();

        if (message) {
          this.showError(message);
        } else {
          console.warn('[email-toast] Unexpected sign-up response', response.status, response.url, doc.title);
          this.showError(GENERIC_ERROR);
        }
      } catch (e) {
        console.warn('[email-toast] Sign-up request failed', e);
        this.showError(GENERIC_ERROR);
      } finally {
        // Clear the spent token so the next try runs a fresh captcha.
        const token = this.form.elements['h-captcha-response'];
        if (token) token.value = '';
        this.sending = false;
        if (!this.leaving) this.setBusy(false);
      }
    }

    async copyCode() {
      const codeEl = this.querySelector('[data-email-toast-code]');
      const code = codeEl.textContent.trim();
      const status = this.querySelector('[data-email-toast-status]');
      const button = this.querySelector('[data-email-toast-copy]');
      try {
        await navigator.clipboard.writeText(code);
        const label = button.textContent;
        button.textContent = 'Copied';
        status.textContent = `Copied ${code} to clipboard`;
        setTimeout(() => (button.textContent = label), 2000);
      } catch (e) {
        const range = document.createRange();
        range.selectNodeContents(codeEl);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  }

  customElements.define('email-toast', EmailToast);
}
